import { findDurationConflict } from "@/utils/durationConflict";
import type { Booking } from "@/types/Booking";
import { endTimeToMinutes } from "@/utils/bookingTime";

/**
 * Per-edge availability for the "Change Time" steppers.
 *
 * The booking has two independently-movable edges. For each edge we test a single
 * 30-minute nudge and report whether it is allowed, plus a short reason when it isn't:
 *   • start earlier / end later  — GROW the booking; gated by findDurationConflict
 *     (R5–R7) and the day boundary.
 *   • start later / end earlier   — SHRINK the booking; never clash, bounded only by
 *     the 30-minute minimum and the edges not crossing.
 */

const STEP_MINUTES = 30;
const MIN_DURATION_MINUTES = 30;
const DAY_END_MINUTES = 24 * 60;

const hhmmToMinutes = (hhmm: string): number => {
  const padded = hhmm.padStart(4, "0");
  return parseInt(padded.slice(0, 2), 10) * 60 + parseInt(padded.slice(2, 4), 10);
};

const minutesToHhmm = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, "0")}${m.toString().padStart(2, "0")}`;
};

export interface TimeAdjustCandidate {
  startTime: string;
  endTime: string;
}

export interface TimeAdjustAvailability {
  canStartEarlier: boolean;
  canStartLater: boolean;
  canEndEarlier: boolean;
  canEndLater: boolean;
  reasons: {
    startEarlier?: string;
    startLater?: string;
    endEarlier?: string;
    endLater?: string;
  };
}

type CurrentBooking = Pick<Booking, "id" | "memberId" | "hasBuffers" | "startTime" | "endTime">;

export function computeTimeAdjustOptions(
  candidate: TimeAdjustCandidate,
  booking: CurrentBooking,
  dailyBookings: ReadonlyArray<Booking>,
  step: number = STEP_MINUTES,
  // The booking's organisation party — when supplied, the org's own bookings may butt back-to-back
  // (buffer waived) when growing a booking, exactly as the same member's. Omit for non-org bookings.
  sameOrgMemberIds?: ReadonlySet<string>,
): TimeAdjustAvailability {
  const start = hhmmToMinutes(candidate.startTime);
  const end = endTimeToMinutes(candidate.endTime, candidate.startTime);
  const reasons: TimeAdjustAvailability["reasons"] = {};

  // Start earlier — grow at the front.
  let canStartEarlier = true;
  const newStart = start - step;
  if (newStart < 0) {
    canStartEarlier = false;
    reasons.startEarlier = "That would start before the day begins.";
  } else if (
    findDurationConflict({ startTime: minutesToHhmm(newStart), endTime: candidate.endTime }, booking, dailyBookings, sameOrgMemberIds)
  ) {
    canStartEarlier = false;
    reasons.startEarlier = "The room is booked just before this time.";
  }

  // Start later — shrink from the front.
  let canStartLater = true;
  if (end - (start + step) < MIN_DURATION_MINUTES) {
    canStartLater = false;
    reasons.startLater = "Bookings must be at least 30 minutes.";
  }

  // End earlier — shrink from the back.
  let canEndEarlier = true;
  if (end - step - start < MIN_DURATION_MINUTES) {
    canEndEarlier = false;
    reasons.endEarlier = "Bookings must be at least 30 minutes.";
  }

  // End later — grow at the back.
  let canEndLater = true;
  const newEnd = end + step;
  if (newEnd >= DAY_END_MINUTES) {
    canEndLater = false;
    reasons.endLater = "That would run past the end of the day.";
  } else if (
    findDurationConflict({ startTime: candidate.startTime, endTime: minutesToHhmm(newEnd) }, booking, dailyBookings, sameOrgMemberIds)
  ) {
    canEndLater = false;
    reasons.endLater = "The room is booked just after this time.";
  }

  return { canStartEarlier, canStartLater, canEndEarlier, canEndLater, reasons };
}
